import { useRef, useState, type FormEvent } from 'react';
import { errorMessage, isApiError } from '@/core/http';
import { Button, cn, Dialog, InlineText, Input } from '@/core/ui';
import {
  ArrowDown,
  ArrowUp,
  FileDoc,
  FileImage,
  FilePdf,
  FilePpt,
  FileText,
  FileXls,
  LinkSimple,
  Trash,
  UploadSimple,
  X,
  type Icon,
} from '@/core/ui/icons';
import {
  useAddMaterialLink,
  useDeleteMaterial,
  useMaterialUploads,
  useRenameMaterial,
  useReorderMaterials,
} from '../application/materials';
import { movedLesson } from '../domain/course';
import {
  formatFileSize,
  linkProblem,
  MATERIAL_ACCEPT,
  MATERIAL_FAMILY_LABELS,
  MATERIAL_TITLE_MAX,
  materialFamily,
  materialTitleProblem,
  URL_MAX,
  type LessonMaterial,
  type MaterialFamily,
} from '../domain/materials';
import { collapseSpaces } from '../domain/schemas';

const FAMILY_ICONS: Record<MaterialFamily, Icon> = {
  pdf: FilePdf,
  image: FileImage,
  doc: FileDoc,
  slides: FilePpt,
  sheet: FileXls,
  link: LinkSimple,
  file: FileText,
};

interface Props {
  courseId: string;
  lessonId: string;
  lessonTitle: string;
  materials: LessonMaterial[];
  canEdit: boolean;
}

/**
 * Materiais complementares de uma aula (lâmina do produto, PDF da aula,
 * links): enviar arquivos (arrastar ou escolher, com progresso), incluir
 * link, renomear no lugar, reordenar e excluir com confirmação.
 */
export function LessonMaterialsPanel({ courseId, lessonId, lessonTitle, materials, canEdit }: Props) {
  const rename = useRenameMaterial(courseId, lessonId);
  const reorder = useReorderMaterials(courseId, lessonId);
  const remove = useDeleteMaterial(courseId, lessonId);
  const { uploads, add, dismiss } = useMaterialUploads(courseId, lessonId);
  const [toDelete, setToDelete] = useState<LessonMaterial | null>(null);
  const ids = materials.map((m) => m.id);

  const closeDelete = () => {
    remove.reset();
    setToDelete(null);
  };

  return (
    <section aria-label={`Materiais de "${lessonTitle}"`} className="rounded-xl bg-surface-2/50 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-fg">Materiais da aula</p>
        {canEdit && (
          <p className="text-xs text-fg-subtle">PDF, imagens, Word, PowerPoint e Excel · até 100 MB</p>
        )}
      </div>

      {materials.length === 0 && uploads.length === 0 && (
        <p className="mt-2 text-sm text-fg-subtle">
          {canEdit
            ? 'Nenhum material ainda. Os alunos veem os materiais logo abaixo do vídeo.'
            : 'Nenhum material nesta aula.'}
        </p>
      )}

      {materials.length > 0 && (
        <ol className={cn('mt-3 divide-y divide-line', reorder.isPending && 'opacity-60')}>
          {materials.map((m, i) => {
            const family = materialFamily(m);
            const FamilyIcon = FAMILY_ICONS[family];
            return (
              <li key={m.id} className="group/material flex items-center gap-3 py-2.5">
                <FamilyIcon aria-hidden className="size-5 shrink-0 text-brand-500" />
                <div className="min-w-0 flex-1">
                  <InlineText
                    label={`título do material "${m.title}"`}
                    value={m.title}
                    readOnly={!canEdit}
                    maxLength={MATERIAL_TITLE_MAX}
                    normalize={collapseSpaces}
                    validate={materialTitleProblem}
                    describeError={materialProblemMessage}
                    onSave={(title) => rename.mutateAsync({ id: m.id, title })}
                    className="truncate text-sm font-medium"
                  />
                  <p className="truncate text-xs text-fg-subtle">
                    {m.kind === 'link' && m.url ? (
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-fg hover:underline"
                      >
                        {m.url}
                      </a>
                    ) : (
                      [
                        MATERIAL_FAMILY_LABELS[family],
                        m.filename,
                        m.size_bytes ? formatFileSize(m.size_bytes) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    )}
                  </p>
                </div>
                {canEdit && (
                  <div className="flex gap-0.5 opacity-0 transition-opacity group-focus-within/material:opacity-100 group-hover/material:opacity-100">
                    {materials.length > 1 && (
                      <>
                        <SmallButton
                          label={`Subir "${m.title}"`}
                          icon={ArrowUp}
                          disabled={i === 0 || reorder.isPending}
                          onClick={() => reorder.mutate(movedLesson(ids, m.id, -1))}
                        />
                        <SmallButton
                          label={`Descer "${m.title}"`}
                          icon={ArrowDown}
                          disabled={i === materials.length - 1 || reorder.isPending}
                          onClick={() => reorder.mutate(movedLesson(ids, m.id, 1))}
                        />
                      </>
                    )}
                    <SmallButton label={`Excluir "${m.title}"`} icon={Trash} onClick={() => setToDelete(m)} />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {reorder.isError && <p className="mt-2 text-sm text-fg">{materialProblemMessage(reorder.error)}</p>}

      {uploads.length > 0 && (
        <ul className="mt-3 space-y-2" aria-label="Envios em andamento">
          {uploads.map((u) => (
            <li key={u.id} className="rounded-lg bg-surface px-3 py-2">
              <div className="flex items-center gap-2">
                <p className="min-w-0 flex-1 truncate text-sm text-fg">{u.name}</p>
                {u.error ? (
                  <SmallButton label={`Dispensar "${u.name}"`} icon={X} onClick={() => dismiss(u.id)} />
                ) : (
                  <span className="text-xs text-fg-subtle tabular-nums">{Math.round(u.progress * 100)}%</span>
                )}
              </div>
              {u.error ? (
                <p role="alert" className="mt-0.5 text-xs text-fg">
                  {u.error}
                </p>
              ) : (
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-surface-2">
                  <div
                    className="h-full rounded-full bg-brand-400 transition-[width]"
                    style={{ width: `${Math.round(u.progress * 100)}%` }}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <FileDrop onFiles={add} />
          <LinkForm courseId={courseId} lessonId={lessonId} />
        </div>
      )}

      <Dialog open={!!toDelete} onClose={closeDelete} title="Excluir material?" description={toDelete?.title}>
        <p className="text-sm text-fg-muted">
          {toDelete?.kind === 'file'
            ? 'O arquivo sai da aula e do armazenamento. Não dá para desfazer.'
            : 'O link sai da aula. Não dá para desfazer.'}
        </p>
        {remove.isError && (
          <p role="alert" className="mt-3 text-sm text-fg">
            {materialProblemMessage(remove.error)}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={closeDelete}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => toDelete && remove.mutate(toDelete.id, { onSuccess: closeDelete })}
          >
            Excluir material
          </Button>
        </div>
      </Dialog>
    </section>
  );
}

/** Área de soltar arquivos (ou clicar e escolher) — aceita vários de uma vez. */
function FileDrop({ onFiles }: { onFiles: (files: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        accept={MATERIAL_ACCEPT}
        className="hidden"
        aria-label="Arquivos de material"
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          onFiles(Array.from(e.dataTransfer.files ?? []));
        }}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line-strong bg-surface px-4 py-5 text-center transition-colors hover:border-brand-400 hover:bg-brand-50',
          over && 'border-brand-400 bg-brand-50',
        )}
      >
        <UploadSimple aria-hidden className="size-5 text-brand-500" />
        <span className="text-sm font-medium text-fg">Enviar arquivos ou arrastar aqui</span>
        <span className="text-xs text-fg-subtle">Lâmina do produto, PDF da aula, planilhas…</span>
      </button>
    </>
  );
}

function LinkForm({ courseId, lessonId }: { courseId: string; lessonId: string }) {
  const addLink = useAddMaterialLink(courseId, lessonId);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const issue = materialTitleProblem(title) ?? linkProblem(url);
    setProblem(issue);
    if (issue) return;
    addLink.mutate(
      { title: collapseSpaces(title), url: url.trim() },
      {
        onSuccess: () => {
          setTitle('');
          setUrl('');
        },
      },
    );
  };

  const error = problem ?? (addLink.isError ? materialProblemMessage(addLink.error) : null);

  return (
    <form onSubmit={submit} noValidate aria-label="Novo link" className="space-y-2 rounded-xl bg-surface p-3">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Título do link"
        aria-label="Título do link"
        maxLength={MATERIAL_TITLE_MAX}
      />
      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
          aria-label="Endereço do link"
          inputMode="url"
          maxLength={URL_MAX}
          className="min-w-0 flex-1"
        />
        <Button type="submit" variant="secondary" loading={addLink.isPending}>
          <LinkSimple aria-hidden className="size-4" />
          Incluir link
        </Button>
      </div>
      {error && <p className="text-xs text-fg">{error}</p>}
    </form>
  );
}

function SmallButton({
  label,
  icon: Icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: Icon;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded-md p-1.5 text-fg-subtle hover:bg-surface-2 hover:text-fg disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent"
    >
      <Icon className="size-3.5" />
    </button>
  );
}

/** Por que a edição não aconteceu, em português. */
function materialProblemMessage(e: unknown): string {
  if (!isApiError(e)) return 'Não foi possível salvar. Tente de novo.';
  if (e.code === 'network_error') return 'Sem conexão com o servidor. Nada foi alterado.';
  if (e.status === 403) return 'Você não tem permissão para editar este curso.';
  if (e.status === 404) return 'Este material não existe mais. Recarregue a página.';
  if (e.code === 'too_many_materials') return 'Esta aula já tem o máximo de materiais.';
  if (e.code === 'invalid_material') return 'Valor não aceito. Confira e tente de novo.';
  return errorMessage(e);
}
